class VideoHotspots extends window.BAO.CustomElement() {
  connectedCallback () {
    super.connectedCallback()

    this.videoPlaying = false

    this.lastScrollY = window.scrollY

    if (this.video.readyState === 0) {
      this.video.addEventListener(
        'progress',
        () => {
          this.configureCanvas()
        },
        { once: true },
      )
    } else {
      this.configureCanvas()
    }

    // eslint-disable-next-line compat/compat
    this.observer = new IntersectionObserver(
      this.intersectionObserverCallback.bind(this),
      {
        threshold: this.threshold,
      },
    )

    window.setTimeout(() => {
      this.observer.observe(this.container)
    }, 200)
  }

  setupListeners () {
    super.setupListeners()

    if (this.els.desktopVideo?.element) {
      this.listeners.add(this.desktopVideoEl, 'seeked', () => {
        this.seeked = true
      })
    }

    if (this.els.mobileVideo?.element) {
      this.listeners.add(this.mobileVideoEl, 'seeked', () => {
        this.seeked = true
      })
    }

    this.configureCanvas = this.configureCanvas.bind(this)
    this.animateVideo = this.animateVideo.bind(this)

    this.listeners.add(window, 'resize', this.configureCanvas.bind(this))

    if (this.container) {
      this.listeners.add(
        window,
        'scroll',
        this.toggleContainerScrollClass.bind(this),
      )
    }
  }

  intersectionObserverCallback (entries) {
    if (!entries?.length) return

    entries.forEach(entry => {
      if (this.videoPlaying) return

      if (entry.isIntersecting) {
        this.videoPlaying = true

        this.els?.intersectionVideo?.element?.addEventListener(
          'loadeddata',
          this.setVideo(),
        )
      }
    })
  }

  setVideo () {
    this.els?.intersectionVideo?.element?.play()
  }

  configureCanvas () {
    if (
      (window.innerWidth >= 901 && this.canvasOriginalViewport === 'desktop') ||
      (window.innerWidth <= 901 && this.canvasOriginalViewport === 'mobile')
    ) {
      return
    }

    this.canvasOriginalViewport = window.innerWidth > 901 ? 'desktop' : 'mobile'

    this.ctx = this.els.canvas.element.getContext('2d')
    this.els.canvas.element.height = this.video.videoHeight
    this.els.canvas.element.width = this.video.videoWidth

    this.end = parseInt(this.video.duration) / 100
    this.seeked = false
    this.currentTime = 0.01

    requestAnimationFrame(this.animateVideo)
  }

  animateVideo () {
    requestAnimationFrame(this.animateVideo)

    if (!this.seeked) return

    const canvas = this.els.canvas.element
    const container = this.els.container.element
    const containerPos = container.getBoundingClientRect()

    if (isNaN(this.video.duration)) {
      return
    }

    const announcementBar = document.querySelector('header-announcement-bar')
    const announcementBarHeight =
      announcementBar !== null ? announcementBar.offsetHeight : 0
    const headerEl = document.querySelector('.hd-Header')
    const headerHeight = headerEl?.offsetHeight || 0
    let combinedHeight
    let containerPosTop

    if (window.innerWidth <= 901) {
      containerPosTop = containerPos.top - 200
      combinedHeight = announcementBarHeight + headerHeight + 200
    } else {
      containerPosTop = containerPos.top
      combinedHeight = announcementBarHeight + headerHeight
    }
    const scrollPercent =
      -(containerPosTop - combinedHeight) /
      (container.offsetHeight - this.video.offsetHeight)

    const duration = Number(this.video.duration)
    let currentTime = duration * scrollPercent

    if (scrollPercent > 1 || scrollPercent < 0) {
      return
    }

    if (currentTime < 0) {
      currentTime = 0
    }

    if (currentTime > duration) {
      currentTime = duration
    }

    if (currentTime === this.currentTime) {
      return
    }

    this.seeked = false

    this.currentTime = currentTime

    this.ctx.clearRect(0, 0, canvas.width, canvas.height)
    this.ctx.drawImage(this.video, 0, 0, canvas.width, canvas.height)

    this.hotSpots.forEach(hotSpot => {
      this.animateHotSpot(hotSpot, scrollPercent)
    })
  }

  animateHotSpot (hotSpot, scrollPercentage) {
    scrollPercentage = scrollPercentage * 100
    let positionY

    window.matchMedia('(width <= 900px)')
      ? (positionY = hotSpot.dataset.posYMob)
      : (positionY = hotSpot.dataset.posY)

    scrollPercentage >= positionY
      ? this.showHotSpot(hotSpot)
      : this.hideHotSpot(hotSpot)
  }

  toggleContainerScrollClass () {
    const currentScrollY = window.scrollY

    currentScrollY >= this.lastScrollY
      ? this.removeScrollClass()
      : this.addScrollClass()

    this.lastScrollY = currentScrollY
  }

  addScrollClass () {
    this.container.classList.add('sec-VideoHotspots_VideoContainer-scrollingUp')
  }

  removeScrollClass () {
    this.container.classList.remove(
      'sec-VideoHotspots_VideoContainer-scrollingUp',
    )
  }

  showHotSpot (hotSpot) {
    hotSpot.classList.add('sec-VideoHotSpots_Hotspot-active')
  }

  hideHotSpot (hotSpot) {
    hotSpot.classList.remove('sec-VideoHotSpots_Hotspot-active')
  }

  get videoDuration () {
    return this.video.duration
  }

  get video () {
    return window.innerWidth >= 901 ? this.desktopVideoEl : this.mobileVideoEl
  }

  get desktopVideoEl () {
    return this.els.desktopVideo.element.querySelector('video')
  }

  get mobileVideoEl () {
    return this.els.mobileVideo.element.querySelector('video')
  }

  get intersectionVideoEl () {
    return this.els?.intersectionVideo?.element
  }

  get currentTime () {
    return this._currentTime
  }

  set currentTime (time) {
    this._currentTime = time
    this.video.currentTime = parseFloat(time.toFixed(2))
  }

  get seeked () {
    return this._seeked
  }

  set seeked (seeked) {
    this._seeked = seeked
  }

  get threshold () {
    return this.hasAttribute('threshold')
      ? Number(this.getAttribute('threshold'))
      : 0
  }

  get hotSpots () {
    return this.els?.hotspot?.elements
  }

  get container () {
    return this.els?.videoContainer?.element
  }
}

if (!customElements.get('video-hotspots')) {
  customElements.define('video-hotspots', VideoHotspots)
}
